import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { setActiveWorkspaceId } from "@/lib/active-workspace";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";

/**
 * WorkspaceContext - tracks the user's active Interview Workspace.
 * Backend auto-creates a default workspace on first list call, so we can safely
 * assume a workspace always exists. `active` is persisted in localStorage so
 * page reloads and navigation preserve the user's selection.
 */

const LS_KEY = "or.activeWorkspaceId";
const WorkspaceContext = createContext(null);

export function WorkspaceProvider({ children }) {
  const { user } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [activeId, setActiveIdState] = useState(() => {
    const stored = localStorage.getItem(LS_KEY) || null;
    // Prime the cross-cutting module immediately so the axios interceptor picks
    // it up on the very first request (before <WorkspaceProvider> mounts).
    setActiveWorkspaceId(stored);
    return stored;
  });
  const [premium, setPremium] = useState(false);
  const [canCreateMore, setCanCreateMore] = useState(true);
  const [loading, setLoading] = useState(true);

  // Keep the axios interceptor's view of the active workspace id in sync with
  // React state - every time it changes, all subsequent requests carry the
  // correct header.
  useEffect(() => {
    setActiveWorkspaceId(activeId);
  }, [activeId]);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/workspaces");
      const list = data?.workspaces || [];
      setWorkspaces(list);
      setPremium(!!data?.premium);
      setCanCreateMore(!!data?.can_create_more);
      // Ensure activeId is valid - fall back to the first active workspace.
      if (!list.some((w) => w.workspace_id === activeId)) {
        const first = list.find((w) => w.status === "active") || list[0];
        if (first) {
          setActiveIdState(first.workspace_id);
          localStorage.setItem(LS_KEY, first.workspace_id);
          setActiveWorkspaceId(first.workspace_id);
        }
      }
    } catch {
      // Silently ignore (user is likely unauthenticated on public pages).
    } finally {
      setLoading(false);
    }
  }, [activeId]);

  // Fetch whenever the auth state resolves to an authenticated user. This
  // handles both cold loads (localStorage rehydrate) and post-login navigation
  // where WorkspaceProvider was already mounted with an unauthenticated user.
  // Only clear local state on a real logout transition (auth → unauthenticated),
  // never on initial mount, so we don't lose the stored active workspace before
  // the auth context has a chance to hydrate.
  const wasAuthedRef = React.useRef(false);
  useEffect(() => {
    if (user) {
      wasAuthedRef.current = true;
      refresh();
    } else if (wasAuthedRef.current) {
      // Real logout - clear the local state so the next user doesn't inherit it.
      wasAuthedRef.current = false;
      setWorkspaces([]);
      setActiveIdState(null);
      localStorage.removeItem(LS_KEY);
      setActiveWorkspaceId(null);
      setLoading(false);
    }
  }, [user?.user_id]);

  const setActive = useCallback((wsId) => {
    setActiveIdState((prev) => {
      // Only fire the "switched" toast on a REAL change (skip first-hydrate
      // and no-op clicks on the currently-active workspace).
      if (prev && wsId && prev !== wsId) {
        const target = workspaces.find((w) => w.workspace_id === wsId);
        const label = [target?.job_title, target?.company].filter(Boolean).join(" @ ");
        toast.success(label ? `Switched to ${label}` : "Workspace switched", {
          description: "Everything on screen now reflects this position.",
          duration: 2600,
        });
      }
      return wsId;
    });
    localStorage.setItem(LS_KEY, wsId);
    setActiveWorkspaceId(wsId);
  }, [workspaces]);

  // Archive / restore. Both are soft - backend flips workspace.status. If the
  // user archives the currently-active workspace we auto-switch to the next
  // active one. Sonner toast exposes an Undo affordance for 6 seconds.
  const archiveWorkspace = useCallback(async (wsId) => {
    const target = workspaces.find((w) => w.workspace_id === wsId);
    if (!target) return;
    // Refuse if it's the last active workspace - the user always needs one.
    const activeCount = workspaces.filter((w) => w.status !== "archived").length;
    if (activeCount <= 1) {
      toast.error("Keep at least one active interview", {
        description: "Create another workspace before archiving this one.",
      });
      return;
    }
    try {
      await api.patch(`/workspaces/${wsId}`, { status: "archived" });
      // If we just archived the active workspace, switch to another active one.
      if (activeId === wsId) {
        const next = workspaces.find(
          (w) => w.workspace_id !== wsId && w.status !== "archived",
        );
        if (next) {
          setActiveIdState(next.workspace_id);
          localStorage.setItem(LS_KEY, next.workspace_id);
          setActiveWorkspaceId(next.workspace_id);
        }
      }
      await refresh();
      toast.success("Interview archived", {
        description: target.name ? `"${target.name}" moved to archive.` : "Moved to archive.",
        duration: 6000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.patch(`/workspaces/${wsId}`, { status: "active" });
              await refresh();
              toast.success("Restored", { duration: 2200 });
            } catch (e) {
              toast.error("Couldn't restore. Try again from All Interviews.");
            }
          },
        },
      });
    } catch (e) {
      toast.error("Couldn't archive that interview", {
        description: e?.response?.data?.detail || "Please try again.",
      });
    }
  }, [workspaces, activeId, refresh]);

  const active = useMemo(
    () => workspaces.find((w) => w.workspace_id === activeId) || null,
    [workspaces, activeId]
  );

  return (
    <WorkspaceContext.Provider
      value={{ workspaces, active, activeId, setActive, refresh, premium, canCreateMore, loading, archiveWorkspace }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error("useWorkspace must be used inside <WorkspaceProvider>");
  return ctx;
}
